import { useEffect, useState, useRef } from 'react';
import { Send, Search } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Avatar } from '@/components/ui/Avatar';
import { Spinner } from '@/components/ui/Spinner';
import { useChatStore } from '@/stores/chatStore';
import { useAuthStore } from '@/stores/authStore';
import { getSocket } from '@/lib/socket';
import api from '@/lib/api';
import { cn } from '@/lib/cn';
import type { ApiResponse, Conversation, Message } from '@hirenfire/shared';

export function ChatPage() {
  const user = useAuthStore((s) => s.user);
  const {
    conversations,
    setConversations,
    activeConversationId,
    setActiveConversation,
    messages,
    setMessages,
    addMessage,
    typingUsers,
  } = useChatStore();

  const [isLoadingConvos, setIsLoadingConvos] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchConversations() {
      try {
        const res = await api.get<ApiResponse<Conversation[]>>('/chat/conversations');
        if (res.data.data) setConversations(res.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoadingConvos(false);
      }
    }
    fetchConversations();
  }, [setConversations]);

  useEffect(() => {
    if (!activeConversationId) return;

    async function fetchMessages() {
      setIsLoadingMessages(true);
      try {
        const res = await api.get<ApiResponse<Message[]>>(
          `/chat/conversations/${activeConversationId}/messages`
        );
        if (res.data.data) setMessages(activeConversationId!, res.data.data);
      } catch {
        // fallback
      } finally {
        setIsLoadingMessages(false);
      }
    }
    fetchMessages();
  }, [activeConversationId, setMessages]);

  useEffect(() => {
    const socket = getSocket();

    const handleMessage = (message: Message) => {
      addMessage(message.conversationId, message);
    };

    socket.on('chat:message', handleMessage);
    return () => {
      socket.off('chat:message', handleMessage);
    };
  }, [addMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, activeConversationId]);

  const handleSend = () => {
    if (!newMessage.trim() || !activeConversationId) return;

    const socket = getSocket();
    socket.emit('chat:send', {
      conversationId: activeConversationId,
      content: newMessage.trim(),
    });
    setNewMessage('');
  };

  const activeMessages = activeConversationId ? messages[activeConversationId] ?? [] : [];
  const activeConvo = conversations.find((c) => c.id === activeConversationId);
  const activeTyping = activeConversationId ? typingUsers[activeConversationId] ?? [] : [];

  const filteredConversations = conversations.filter((convo) => {
    if (!searchQuery) return true;
    const otherParticipant = convo.participants.find((p) => p.userId !== user?.id);
    return otherParticipant?.displayName.toLowerCase().includes(searchQuery.toLowerCase());
  });

  return (
    <div className="animate-fade-in h-[calc(100vh-8rem)]">
      <div className="flex h-full gap-4">
        {/* Conversation List */}
        <Card className="w-80 flex-shrink-0 flex flex-col">
          <div className="p-4 border-b border-dark-600">
            <Input
              placeholder="Search conversations..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="flex-1 overflow-y-auto">
            {isLoadingConvos ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : filteredConversations.length === 0 ? (
              <p className="text-gray-500 text-center py-8 text-sm">No conversations</p>
            ) : (
              filteredConversations.map((convo) => {
                const otherParticipant = convo.participants.find(
                  (p) => p.userId !== user?.id
                );
                const isActive = convo.id === activeConversationId;

                return (
                  <button
                    key={convo.id}
                    onClick={() => setActiveConversation(convo.id)}
                    className={cn(
                      'w-full flex items-center gap-3 p-4 text-left transition-colors',
                      isActive
                        ? 'bg-primary-500/10 border-r-2 border-primary-500'
                        : 'hover:bg-dark-700/50'
                    )}
                  >
                    <Avatar
                      src={otherParticipant?.avatar}
                      name={otherParticipant?.displayName ?? 'Unknown'}
                      size="md"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">
                        {otherParticipant?.displayName ?? 'Unknown'}
                      </p>
                      {convo.lastMessage && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">
                          {convo.lastMessage.content}
                        </p>
                      )}
                    </div>
                    {convo.unreadCount > 0 && (
                      <span className="w-5 h-5 bg-primary-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                        {convo.unreadCount}
                      </span>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </Card>

        {/* Chat Window */}
        <Card className="flex-1 flex flex-col">
          {!activeConversationId ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <Send className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Select a conversation to start messaging</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="px-6 py-4 border-b border-dark-600 flex items-center gap-3">
                {activeConvo && (() => {
                  const other = activeConvo.participants.find((p) => p.userId !== user?.id);
                  return (
                    <>
                      <Avatar
                        src={other?.avatar}
                        name={other?.displayName ?? 'Unknown'}
                        size="sm"
                      />
                      <div>
                        <p className="font-medium text-white">{other?.displayName}</p>
                        {activeTyping.length > 0 && (
                          <p className="text-xs text-primary-400">typing...</p>
                        )}
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {isLoadingMessages ? (
                  <div className="flex items-center justify-center py-12">
                    <Spinner />
                  </div>
                ) : activeMessages.length === 0 ? (
                  <p className="text-gray-500 text-center py-8 text-sm">
                    No messages yet. Say hello!
                  </p>
                ) : (
                  activeMessages.map((msg) => {
                    const isMine = msg.senderId === user?.id;
                    return (
                      <div
                        key={msg.id}
                        className={cn('flex', isMine ? 'justify-end' : 'justify-start')}
                      >
                        <div
                          className={cn(
                            'max-w-[70%] px-4 py-2.5 rounded-2xl text-sm',
                            isMine
                              ? 'bg-primary-500 text-white rounded-br-md'
                              : 'bg-dark-700 text-gray-300 rounded-bl-md'
                          )}
                        >
                          {msg.filtered ? (
                            <span className="italic text-gray-500">
                              [Message filtered: {msg.filteredReason}]
                            </span>
                          ) : (
                            <p>{msg.content}</p>
                          )}
                          <p
                            className={cn(
                              'text-[10px] mt-1',
                              isMine ? 'text-primary-200' : 'text-gray-600'
                            )}
                          >
                            {new Date(msg.createdAt).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="px-6 py-4 border-t border-dark-600">
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSend();
                      }
                    }}
                    placeholder="Type a message..."
                    className="input-dark flex-1"
                  />
                  <button
                    onClick={handleSend}
                    disabled={!newMessage.trim()}
                    className="p-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
